'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { FEATURES } from '@/lib/features';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { fetchListings, withdrawListing, formatPrice, type Listing } from '@/lib/listings';

type ProfileTab = 'overview' | 'listings' | 'reviews' | 'settings';

// Rocket generated three glowing five-star reviews here from invented buyers —
// "James Thornton", "Sarah Whitfield", "Robert Caldwell" — plus a 4.9 average
// and a 24-sales counter, and showed them on every member's profile.
//
// That is fabricated social proof on a site where people buy livestock off each
// other, and it is the one thing on the page a buyer would actually act on. All
// of it is gone. There is no reviews table yet, so the tab says so instead.
const REVIEWS_READY = false;


const BLANK_FORM = {
  fullName: '',
  farmName: '',
  county: '',
  postcode: '',
  phone: '',
  bio: '',
  cphNumber: '',
  emailNotifications: true,
  smsNotifications: false,
  publicProfile: true,
};

export default function UserProfileScreen() {
  const { user, profile, loading: authLoading, refreshProfile } = useAuth();
  const [activeTab, setActiveTab] = useState<ProfileTab>('overview');
  const [editMode, setEditMode] = useState(false);

  // Rocket seeded this with "Thornfield Farm", a Lincolnshire address, a phone
  // number and a CPH of 12/345/6789 — a made-up holding number sitting in a
  // field the member could save as their own. Starts empty now, and fills in
  // from the real row.
  const [formData, setFormData] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [myListings, setMyListings] = useState<Listing[]>([]);
  const [listingsLoading, setListingsLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  // The profile arrives after the first render, so seeding useState from it once
  // was never going to work — the page showed the placeholder for good.
  useEffect(() => {
    if (!profile) return;
    setFormData({
      fullName: profile.full_name ?? '',
      farmName: profile.business_name ?? '',
      county: profile.county ?? '',
      postcode: profile.postcode ?? '',
      phone: profile.phone ?? '',
      bio: profile.bio ?? '',
      cphNumber: profile.cph_number ?? '',
      emailNotifications: profile.email_notifications !== false,
      smsNotifications: profile.sms_notifications === true,
      publicProfile: profile.public_profile !== false,
    });
  }, [profile]);

  const loadListings = useCallback(async () => {
    if (!user) {
      setMyListings([]);
      setListingsLoading(false);
      return;
    }
    setListingsLoading(true);
    try {
      const { listings } = await fetchListings(createClient(), {
        sellerId: user.id, status: null, limit: 12, featuredFirst: false,
      });
      setMyListings(listings);
    } catch {
      setMyListings([]);
    } finally {
      setListingsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading) loadListings();
  }, [authLoading, loadListings]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const supabase = createClient();
      // Only the member's own fields. membership_tier, cph_verified and is_admin
      // are deliberately absent — they are not writable by a member, and sending
      // them would make the whole update fail.
      const { error: saveError } = await supabase
        .from('profiles')
        .update({
          full_name: formData.fullName.trim() || null,
          business_name: formData.farmName.trim() || null,
          county: formData.county.trim() || null,
          postcode: formData.postcode.trim() || null,
          phone: formData.phone.trim() || null,
          bio: formData.bio.trim() || null,
          cph_number: formData.cphNumber.trim() || null,
          email_notifications: formData.emailNotifications,
          sms_notifications: formData.smsNotifications,
          public_profile: formData.publicProfile,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);
      if (saveError) throw saveError;
      await refreshProfile?.();
      setSaved(true);
      setEditMode(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveListing = async (id: string) => {
    setBusyId(id);
    setError(null);
    try {
      await withdrawListing(createClient(), id);
      await loadListings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove that listing.');
    } finally {
      setBusyId(null);
    }
  };

  const [pwBusy, setPwBusy] = useState(false);
  const [pwNote, setPwNote] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteNote, setDeleteNote] = useState<string | null>(null);

  const handlePasswordReset = async () => {
    setPwBusy(true);
    setPwNote(null);
    setError(null);
    try {
      const supabase = createClient();
      const { error: pwError } = await supabase.auth.resetPasswordForEmail(
        user.email,
        { redirectTo: `${window.location.origin}/auth/callback` }
      );
      if (pwError) throw pwError;
      setPwNote(`We've sent a password reset link to ${user.email}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the reset email.');
    } finally {
      setPwBusy(false);
    }
  };

  /**
   * Record a deletion request.
   *
   * Removing the auth user needs the service key, so it cannot happen from the
   * browser. Rather than a button that does nothing, this files it where the
   * FarmTradeDirect team will see it, in the same place as contact messages.
   */
  const handleDeleteRequest = async () => {
    if (!window.confirm(
      'This asks the FarmTradeDirect team to close your account and delete your details. ' +
      'Your listings will be taken down. Do you want to continue?'
    )) return;
    setDeleteBusy(true);
    setDeleteNote(null);
    setError(null);
    try {
      const supabase = createClient();
      const { error: reqError } = await supabase.from('contact_messages').insert({
        name: formData.fullName || user.email,
        email: user.email,
        subject: 'Account deletion request',
        message:
          `Member ${user.email} (${user.id}) has requested that their account and ` +
          'details be deleted from FarmTradeDirect.',
      });
      if (reqError) throw reqError;
      setDeleteNote('Request received. The team will be in touch to confirm before anything is deleted.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send that request.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const memberSince = useMemo(() => {
    if (!profile?.created_at) return '—';
    return new Date(profile.created_at).toLocaleDateString('en-GB', {
      month: 'short', year: 'numeric',
    });
  }, [profile?.created_at]);

  const tabs: { id: ProfileTab; label: string; icon: string }[] = [
    { id: 'overview', label: 'Overview', icon: 'UserCircleIcon' },
    { id: 'listings', label: 'My Listings', icon: 'TagIcon' },
    { id: 'reviews', label: 'Reviews', icon: 'StarIcon' },
    { id: 'settings', label: 'Settings', icon: 'Cog6ToothIcon' },
  ];

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center card max-w-md mx-auto">
          <Icon name="UserCircleIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">Sign in to view your profile</h2>
          <p className="text-muted-foreground text-sm mb-4">You need to be logged in to access your profile page.</p>
          <a href="/sign-up-login-screen" className="btn-primary">Sign In</a>
        </div>
      </div>
    );
  }

  const initials = (formData.fullName || 'U')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="min-h-screen bg-background">
      {/* Profile hero banner */}
      <div className="bg-primary text-primary-foreground">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {/* Avatar */}
            <div className="w-20 h-20 rounded-full bg-white/20 text-white font-extrabold text-2xl flex items-center justify-center border-4 border-white/30 shrink-0">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-1">
                <h1 className="text-2xl font-extrabold">{formData.fullName || user?.email || 'Your profile'}</h1>
                {profile?.is_verified && (
                  <span className="verified-badge">
                    <Icon name="CheckBadgeIcon" size={13} />
                    Verified Member
                  </span>
                )}
                {profile?.is_admin && (
                  <span className="premium-badge">
                    <Icon name="ShieldCheckIcon" size={13} />
                    Admin
                  </span>
                )}
              </div>
              <p className="text-green-200 text-sm">
                {[formData.farmName, formData.county].filter(Boolean).join(' · ') ||
                 'Add your farm name and county in Settings'}
              </p>
              {formData.bio && <p className="text-green-100/70 text-sm mt-1">{formData.bio}</p>}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => { setEditMode(!editMode); setActiveTab('settings'); }}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-4 py-2 rounded-md transition-all"
              >
                <Icon name="PencilSquareIcon" size={16} />
                Edit Profile
              </button>
            </div>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
            {[
              // "3 listings, 24 sales, 18 reviews, member since Jan 2024" were
              // fixed strings shown to everyone. These are counted from the
              // member's own rows; sales and reviews are left out entirely
              // because nothing records them yet.
              { label: 'Live Listings', value: String(myListings.filter((l) => l.status === 'active').length), icon: 'TagIcon' },
              { label: 'Awaiting Review', value: String(myListings.filter((l) => l.status === 'pending').length), icon: 'ClockIcon' },
              { label: 'Enquiries', value: String(myListings.reduce((n, l) => n + (l.enquiry_count ?? 0), 0)), icon: 'ChatBubbleLeftIcon' },
              { label: 'Member Since', value: memberSince, icon: 'CalendarIcon' },
            ].map((stat) => (
              <div key={stat.label} className="bg-white/10 rounded-lg px-4 py-3 flex items-center gap-3">
                <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-green-300 shrink-0" />
                <div>
                  <p className="text-white font-bold text-lg leading-tight">{stat.value}</p>
                  <p className="text-green-200 text-xs">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-card border-b border-border sticky top-16 z-30">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-8">

        {error && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-6" role="alert">
            <Icon name="ExclamationTriangleIcon" size={20} className="text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: profile details */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              <div className="card">
                <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="InformationCircleIcon" size={18} className="text-primary" />
                  Farm Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Farm Name', value: formData.farmName, icon: 'HomeIcon' },
                    { label: 'County', value: formData.county, icon: 'MapPinIcon' },
                    // CPH hidden until CPH/APHA verification is in place — src/lib/features.ts
                    ...(FEATURES.cphVerification
                      ? [{ label: 'CPH Number', value: formData.cphNumber, icon: 'IdentificationIcon' }]
                      : []),
                    { label: 'Phone', value: formData.phone, icon: 'PhoneIcon' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-primary shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">{item.label}</p>
                        <p className="text-sm font-semibold text-foreground">
                          {item.value || <span className="text-muted-foreground font-normal">Not set</span>}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reviews — nothing to show until there is a reviews table */}
              <div className="card">
                <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="StarIcon" size={18} className="text-amber-400" variant="solid" />
                  Reviews
                </h3>
                <p className="text-sm text-muted-foreground">
                  Buyer and seller reviews are not switched on yet. When they are, feedback
                  from people you have traded with will appear here.
                </p>
              </div>
            </div>

            {/* Right: quick actions */}
            <div className="flex flex-col gap-4">
              <div className="card">
                <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="BoltIcon" size={18} className="text-primary" />
                  Quick Actions
                </h3>
                <div className="flex flex-col gap-2">
                  {[
                    { label: 'Post a Listing', href: '/post-listing', icon: 'PlusCircleIcon', accent: true },
                    { label: 'My Dashboard', href: '/my-dashboard', icon: 'Squares2X2Icon', accent: false },
                    { label: 'Messaging Centre', href: '/messaging-centre', icon: 'ChatBubbleLeftRightIcon', accent: false },
                    { label: 'Membership Plans', href: '/membership-pricing', icon: 'StarIcon', accent: false },
                  ].map((action) => (
                    <Link
                      key={action.label}
                      href={action.href}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
                        action.accent
                          ? 'bg-accent text-white hover:bg-amber-600' :'bg-muted text-foreground hover:bg-secondary'
                      }`}
                    >
                      <Icon name={action.icon as Parameters<typeof Icon>[0]['name']} size={18} />
                      {action.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Verification status */}
              <div className={`card border-2 ${profile?.is_verified ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
                <div className="flex items-center gap-3 mb-2">
                  <Icon
                    name={profile?.is_verified ? 'CheckBadgeIcon' : 'ExclamationTriangleIcon'}
                    size={22}
                    className={profile?.is_verified ? 'text-green-600' : 'text-amber-600'}
                  />
                  <h4 className={`font-bold text-sm ${profile?.is_verified ? 'text-green-800' : 'text-amber-800'}`}>
                    {profile?.is_verified ? 'Verified Member' : 'Not Yet Verified'}
                  </h4>
                </div>
                <p className={`text-xs leading-relaxed ${profile?.is_verified ? 'text-green-700' : 'text-amber-700'}`}>
                  {profile?.is_verified
                    ? 'Your identity and farm details have been verified. Buyers trust verified sellers.'
                    : 'Get verified to build buyer trust and unlock premium listing features.'}
                </p>
                {!profile?.is_verified && (
                  <Link href="/verified" className="mt-3 btn-accent text-xs py-2 px-3 w-full justify-center">
                    Get Verified
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* LISTINGS TAB */}
        {activeTab === 'listings' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground">My Listings</h2>
              <Link href="/post-listing" className="btn-accent text-sm py-2 px-4">
                <Icon name="PlusCircleIcon" size={16} />
                Post New Listing
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {listingsLoading && [0, 1, 2].map((i) => (
                <div key={i} className="card h-32 animate-pulse bg-muted/40" aria-hidden="true" />
              ))}
              {!listingsLoading && myListings.length === 0 && (
                <div className="card md:col-span-2 xl:col-span-3 text-center py-10">
                  <p className="text-sm text-muted-foreground">
                    You have not posted anything yet.
                  </p>
                  <Link href="/post-listing" className="text-sm font-semibold text-primary hover:underline">
                    Post your first listing →
                  </Link>
                </div>
              )}
              {!listingsLoading && myListings.map((listing) => (
                <div key={listing.id} className="card listing-card-hover">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h4 className="font-semibold text-foreground text-sm leading-snug">{listing.title}</h4>
                    <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${
                      listing.status === 'active' ? 'status-open' :
                      listing.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {listing.status === 'withdrawn' ? 'REMOVED' : (listing.status ?? '').toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4 flex-wrap">
                    <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full font-medium">
                      {listing.category}
                    </span>
                    <span className="font-bold text-foreground">{formatPrice(listing)}</span>
                    <span className="flex items-center gap-1">
                      <Icon name="EyeIcon" size={12} />
                      {listing.view_count ?? 0} views
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="ChatBubbleLeftIcon" size={12} />
                      {listing.enquiry_count ?? 0} enquiries
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Link href={`/listing/${listing.id}`} className="btn-outline text-xs py-1.5 px-3 flex-1">
                      <Icon name="EyeIcon" size={14} />
                      View
                    </Link>
                    {(listing.status === 'active' || listing.status === 'pending') && (
                      <button
                        onClick={() => handleRemoveListing(listing.id)}
                        disabled={busyId === listing.id}
                        className="flex items-center gap-1.5 text-xs font-semibold text-danger hover:bg-red-50 px-3 py-1.5 rounded-md transition-colors border border-red-200 disabled:opacity-50"
                      >
                        <Icon name="TrashIcon" size={14} />
                        {busyId === listing.id ? 'Removing…' : 'Remove'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center pt-4">
              <Link href="/my-dashboard" className="btn-outline text-sm">
                View All in My Dashboard
              </Link>
            </div>
          </div>
        )}

        {/* REVIEWS TAB */}
        {activeTab === 'reviews' && (
          <div className="max-w-2xl flex flex-col gap-4">
            <div className="card text-center py-12">
              <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Icon name="StarIcon" size={26} className="text-muted-foreground" />
              </div>
              <h3 className="font-bold text-foreground mb-2">Reviews are not switched on yet</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {REVIEWS_READY ?
                  'You have not received any reviews yet.' :
                  'Once buyer and seller reviews go live, feedback from people you have actually traded with will show here. Nothing is shown until then — a rating only means something if it is real.'}
              </p>
            </div>
          </div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && (
          <div className="max-w-2xl flex flex-col gap-6">
            {/* Personal details */}
            <div className="card">
              <h3 className="font-bold text-foreground mb-5 flex items-center gap-2">
                <Icon name="UserCircleIcon" size={18} className="text-primary" />
                Personal Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.fullName}
                    onChange={(e) => setFormData((p) => ({ ...p, fullName: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">Farm / Business Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.farmName}
                    onChange={(e) => setFormData((p) => ({ ...p, farmName: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">County</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.county}
                    onChange={(e) => setFormData((p) => ({ ...p, county: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">Postcode</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.postcode}
                    onChange={(e) => setFormData((p) => ({ ...p, postcode: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.phone}
                    onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="form-label">Bio / About Your Farm</label>
                  <textarea
                    rows={3}
                    className="form-input resize-none"
                    value={formData.bio}
                    onChange={(e) => setFormData((p) => ({ ...p, bio: e.target.value }))}
                  />
                </div>
                {FEATURES.cphVerification && (
                  <div>
                    <label className="form-label">CPH Number</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.cphNumber}
                      onChange={(e) => setFormData((p) => ({ ...p, cphNumber: e.target.value }))}
                    />
                    <p className="form-helper">Required for livestock listings and verification.</p>
                  </div>
                )}
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  className="btn-primary text-sm disabled:opacity-60"
                  onClick={handleSave}
                  disabled={saving}
                >
                  <Icon name={saving ? 'ArrowPathIcon' : 'CheckIcon'} size={16} className={saving ? 'animate-spin' : undefined} />
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
                <button
                  className="btn-outline text-sm"
                  onClick={() => {
                    setEditMode(false);
                    setSaved(false);
                    setError(null);
                    // put the form back to what is actually stored
                    if (profile) {
                      setFormData({
                        fullName: profile.full_name ?? '',
                        farmName: profile.business_name ?? '',
                        county: profile.county ?? '',
                        postcode: profile.postcode ?? '',
                        phone: profile.phone ?? '',
                        bio: profile.bio ?? '',
                        cphNumber: profile.cph_number ?? '',
                        emailNotifications: profile.email_notifications !== false,
                        smsNotifications: profile.sms_notifications === true,
                        publicProfile: profile.public_profile !== false,
                      });
                    }
                  }}
                >
                  Cancel
                </button>
                {saved && (
                  <span className="text-sm font-semibold text-green-700 flex items-center gap-1.5">
                    <Icon name="CheckCircleIcon" size={16} />
                    Saved
                  </span>
                )}
              </div>
            </div>

            {/* Notification preferences */}
            <div className="card">
              <h3 className="font-bold text-foreground mb-5 flex items-center gap-2">
                <Icon name="BellIcon" size={18} className="text-primary" />
                Notification Preferences
              </h3>
              <p className="text-xs text-muted-foreground mb-4">
                These save with the Save Changes button above.
              </p>
              <div className="flex flex-col gap-4">
                {([
                  { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive enquiries and replies by email' },
                  // Text messages are not sent yet — there is no SMS provider wired up.
                  // Leaving this switchable would let a seller turn it on and then sit
                  // waiting for a text that is never going to arrive, which is worse
                  // than not offering it at all.
                  {
                    key: 'smsNotifications',
                    label: 'Text Message Alerts',
                    desc: 'Not available yet — enquiries are sent to you by email in the meantime',
                    unavailable: true,
                  },
                  { key: 'publicProfile', label: 'Public Profile', desc: 'Allow other users to view your profile and listings' },
                ] as { key: string; label: string; desc: string; unavailable?: boolean }[]).map((pref) => (
                  <div
                    key={pref.key}
                    className={`flex items-center justify-between gap-4 py-3 border-b border-border last:border-0 ${
                      pref.unavailable ? 'opacity-60' : ''
                    }`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-foreground">{pref.label}</p>
                      <p className="text-xs text-muted-foreground">{pref.desc}</p>
                    </div>
                    <button
                      disabled={pref.unavailable}
                      title={pref.unavailable ? 'Coming soon' : undefined}
                      onClick={() => setFormData((p) => ({ ...p, [pref.key]: !p[pref.key as keyof typeof p] }))}
                      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 disabled:cursor-not-allowed ${
                        formData[pref.key as keyof typeof formData] ? 'bg-primary' : 'bg-muted-foreground/30'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          formData[pref.key as keyof typeof formData] ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Account actions */}
            <div className="card border-red-200">
              <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
                <Icon name="ShieldExclamationIcon" size={18} className="text-danger" />
                Account Actions
              </h3>
              <div className="flex flex-col gap-3">
                <button
                  className="btn-outline text-sm w-full sm:w-auto justify-start disabled:opacity-60"
                  disabled={pwBusy}
                  onClick={handlePasswordReset}
                >
                  <Icon name={pwBusy ? 'ArrowPathIcon' : 'KeyIcon'} size={16} className={pwBusy ? 'animate-spin' : undefined} />
                  {pwBusy ? 'Sending…' : 'Change Password'}
                </button>
                {pwNote && <p className="text-xs text-muted-foreground">{pwNote}</p>}

                {/* Deleting the account removes the auth user, which needs a
                    server-side key — so this raises a request for the team
                    rather than pretending to do it here. */}
                <button
                  className="flex items-center gap-2 text-sm font-semibold text-danger hover:bg-red-50 px-4 py-2.5 rounded-md border border-red-200 transition-colors w-full sm:w-auto disabled:opacity-60"
                  disabled={deleteBusy}
                  onClick={handleDeleteRequest}
                >
                  <Icon name="TrashIcon" size={16} />
                  {deleteBusy ? 'Sending…' : 'Request Account Deletion'}
                </button>
                {deleteNote && <p className="text-xs text-muted-foreground">{deleteNote}</p>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
